/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    StationPolicy,
    StreamingRestrictionsInternal,
    StreamingRestrictionType,
} from 'client-code/channels-service';

import { Constants } from '../../constants';
import { ServicesClientImpl } from '../../interactors/ServicesClientImpl';
import { PlaybackContentType } from '../../playback/enums';
import { TInHomeLocation } from '../../types/TInHomeLocation';
import { PlaybackRestrictionType } from '../enums';
import { IRestriction } from '../interfaces';
import { TInHomeRestrictionParams } from '../types';

export class InHomeLocationRestrictionImpl implements IRestriction<TInHomeRestrictionParams> {
    // eslint-disable-next-line class-methods-use-this
    private getRestrictionByStatus(inHomeLocationData: TInHomeLocation): PlaybackRestrictionType {
        if (inHomeLocationData?.status === Constants.IN_HOME_LOCATION_STATUS.ERROR) {
            return PlaybackRestrictionType.INHOME_LOCATION_ERROR;
        }

        if (inHomeLocationData?.status === Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE) {
            return PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE;
        }

        return PlaybackRestrictionType.NO_RESTRICTION;
    }

    // TODO: move to utils
    // eslint-disable-next-line class-methods-use-this
    private getFilteredStationPolicies(
        policyInfo: Array<StationPolicy | undefined>,
        playbackContentType: PlaybackContentType,
    ): StreamingRestrictionsInternal[] {
        let filteredPolicies: StreamingRestrictionsInternal[];

        switch (playbackContentType) {
            case PlaybackContentType.CATCHUP_BEGINNING:
            case PlaybackContentType.CATCHUP_RESUME:
                filteredPolicies = policyInfo
                    .filter((policy) => {
                        return policy?.linearStationRules?.streamingRules?.cuStreamingRestrictions;
                    })
                    .map((policy) => {
                        return policy?.linearStationRules?.streamingRules?.cuStreamingRestrictions!;
                    });
                break;

            case PlaybackContentType.STARTOVER_BEGINNING:
            case PlaybackContentType.STARTOVER_RESUME:
                filteredPolicies = policyInfo
                    .filter((policy) => {
                        return policy?.linearStationRules?.streamingRules?.soStreamingRestrictions;
                    })
                    .map((policy) => {
                        return policy?.linearStationRules?.streamingRules?.soStreamingRestrictions!;
                    });
                break;

            default:
                filteredPolicies = policyInfo
                    .filter((policy) => {
                        return policy?.linearStationRules?.streamingRules
                            ?.linearStreamingRestrictions;
                    })
                    .map((policy) => {
                        return policy?.linearStationRules?.streamingRules
                            ?.linearStreamingRestrictions!;
                    });
        }
        return filteredPolicies;
    }

    // eslint-disable-next-line class-methods-use-this
    private isBlacklist(inHomeLocationData: TInHomeLocation): boolean {
        if (inHomeLocationData.status !== Constants.IN_HOME_LOCATION_STATUS.SUCCESS) return false;
        return !!inHomeLocationData.response?.inBlacklist;
    }

    // eslint-disable-next-line class-methods-use-this
    private isOutOfHome(
        inHomeLocationData: TInHomeLocation,
        playbackContentType: PlaybackContentType,
        policyInfo?: Array<StationPolicy | undefined>,
    ): boolean {
        let outOfHome = false;
        if (inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.SUCCESS) {
            outOfHome = !inHomeLocationData.response?.inHome;
        }

        if (!outOfHome) return false;

        if (!policyInfo?.length) return false;

        const filteredPolicies: StreamingRestrictionsInternal[] = this.getFilteredStationPolicies(
            policyInfo,
            playbackContentType,
        );

        for (let _index = 0; _index < filteredPolicies.length; _index += 1) {
            const streamingRestrictions = filteredPolicies[_index];
            if (streamingRestrictions?.outOfHomeStreaming === StreamingRestrictionType.Blocked) {
                return true;
            }
        }

        return false;
    }

    // eslint-disable-next-line class-methods-use-this
    private isOutOfRegion(inHomeLocationData: TInHomeLocation): boolean {
        if (inHomeLocationData?.status !== Constants.IN_HOME_LOCATION_STATUS.SUCCESS) return false;
        return !inHomeLocationData?.response?.inRegion;
    }

    public checkRestriction(data: TInHomeRestrictionParams): PlaybackRestrictionType {
        const inHomeLocationData = new ServicesClientImpl().getInHomeLocation();
        const serviceStatusRestriction = this.getRestrictionByStatus(inHomeLocationData);

        if (serviceStatusRestriction !== PlaybackRestrictionType.NO_RESTRICTION) {
            return serviceStatusRestriction;
        }

        if (this.isBlacklist(inHomeLocationData)) return PlaybackRestrictionType.BLACKLISTED;

        if (this.isOutOfRegion(inHomeLocationData)) return PlaybackRestrictionType.OUT_OF_REGION;

        if (this.isOutOfHome(inHomeLocationData, data.playbackCOntentType, data.stationPolicy))
            return PlaybackRestrictionType.OUT_OF_HOME;

        return PlaybackRestrictionType.NO_RESTRICTION;
    }
}
